import { ref, readonly } from 'vue';
import { useStorage } from '@/composables/use-storage';
import { fetchChatQuestionsAndAnswers, sendChatQuestionToAPI } from '@/services/api';
import Socket from '@/services/websocket';

let socketInstance = null;


const chatMessage = ref('');
const chatResponse = ref('');
const chatQuestions = ref([]);
const chatError = ref([]);
const chatAnswers = ref([]);
const chatSessionId = ref(null);
const chatListingId = ref('');
const chatModel = ref('gpt-4o-mini');
const isLoading = ref(false);
const chatHistory = ref([]);

export function useChat() {

  const {
    getUserInfo,
    getChatQuestionsAndAnswersFromCache,
    saveChatQuestionsAndAnswersToCache,
  } = useStorage();
  
  const userInfo = getUserInfo();

  const loadFromCache = async () => {
    try {
      const cachedChatQuestionsAndAnswers = getChatQuestionsAndAnswersFromCache();
      if (cachedChatQuestionsAndAnswers) {
        chatQuestions.value = Array.isArray(cachedChatQuestionsAndAnswers) ? cachedChatQuestionsAndAnswers.chatQuestions : [];
        chatAnswers.value = Array.isArray(cachedChatQuestionsAndAnswers) ? cachedChatQuestionsAndAnswers.chatAnswers : [];
        return true;
      }
    } catch (error) {
      console.error('Error loading chat questions and answers from cache:', error);
    }
    
    if (!Array.isArray(chatQuestions.value)) {
      chatQuestions.value = [];
    }
    if (!Array.isArray(chatAnswers.value)) {
      chatAnswers.value = [];
    }
    
    return false;
  };

  const saveToCache = () => {
    return saveChatQuestionsAndAnswersToCache({chatQuestions: chatQuestions.value, chatAnswers: chatAnswers.value});
  };

  const syncChatDataFromStorage = (newValue) => {
    try {
      const data = JSON.parse(newValue);
      const cached = getChatQuestionsAndAnswersFromCache();
      if (cached && data.timestamp > cached.timestamp) {
        chatQuestions.value = Array.isArray(data.chatQuestions) ? data.chatQuestions : [];
        chatAnswers.value = Array.isArray(data.chatAnswers) ? data.chatAnswers : [];
      }
    } catch (error) {
      console.error('Error synchronizing chat history between tabs:', error);
      if (!Array.isArray(chatQuestions.value)) {
        chatQuestions.value = [];
        chatAnswers.value = [];
      }
    }
  };

  const handleStorageChange = (event) => {
    const userCacheKey = `chat_questions_and_answers_${userInfo?.id}`;
    if (event.key === userCacheKey && event.newValue) {
      syncChatDataFromStorage(event.newValue);
    }
  };

  const ensureArrays = () => {
    if (!Array.isArray(chatQuestions.value)) {
      chatQuestions.value = [];
    }
    if (!Array.isArray(chatAnswers.value)) {
      chatAnswers.value = [];
    }
  };
  
  const processCachedData = (cached, listingId) => {
    chatListingId.value = listingId;
    chatQuestions.value = Array.isArray(cached.chatQuestions) ? cached.chatQuestions : [];
    chatAnswers.value = Array.isArray(cached.chatAnswers) ? cached.chatAnswers : [];
    return { chatQuestions: chatQuestions.value, chatAnswers: chatAnswers.value };
  };
  
  const processApiResponse = (response, listingId) => {
    chatListingId.value = listingId;
    chatSessionId.value = response.session_id;
    chatQuestions.value = Array.isArray(response) ? response.map(item => item.question) : [];
    chatAnswers.value = Array.isArray(response) ? response.map(item => item.answer) : [];
    chatHistory.value = Array.isArray(response) ? response.map(item => item.chat_history) : [];
    chatError.value = response.error ? response.error : [];

    saveToCache();
    
    const result = { 
      chatQuestions: chatQuestions.value, 
      chatAnswers: chatAnswers.value, 
      chatHistory: chatHistory.value 
    };
     
    if (chatError.value && Object.keys(chatError.value).length > 0) {
      result.error = chatError.value;
    }
    return result;
  };

  const handleFetchError = (error) => {
    console.error('Error fetching questions and answers:', error);
    
    const shouldLoadFromCache = !Array.isArray(chatQuestions.value) || chatQuestions.value?.length === 0;
    if (shouldLoadFromCache) {
      loadFromCache();
    }
    
    ensureArrays();
  };

  const getChatQuestionsAndAnswers = async (showLoading = true, listingId = null) => {
    if (showLoading) isLoading.value = true;
    
    const cached = getChatQuestionsAndAnswersFromCache();
    const hasCachedData = cached?.chatQuestions && cached.chatQuestions.length > 0;
    
    if (hasCachedData) {
      return processCachedData(cached, listingId);
    }
    
    try {
      const response = userInfo?.id ? await fetchChatQuestionsAndAnswers(listingId) : [];
      return processApiResponse(response, listingId);
    } catch (error) {
      handleFetchError(error);
    } finally {
      if (showLoading) isLoading.value = false;
    }
  };

  const handleChatResponse = (chatMessageData) => {
    if (chatMessageData) {
      chatHistory.value = chatMessageData.chat_history;
      chatResponse.value = chatMessageData.message;
      chatListingId.value = chatMessageData.listing_id;
      chatSessionId.value = chatMessageData.session_id;
      chatModel.value = chatMessageData.model;

      saveToCache();

    }
  };

  const sendChatQuestion = async (message, listingId, sessionId, model, chatHistory) => {
    try {
      chatMessage.value = message;
      chatListingId.value = listingId;
      chatSessionId.value = sessionId;
      chatModel.value = model;
      chatHistory.value = chatHistory;
      const response = await sendChatQuestionToAPI(chatSessionId.value, chatListingId.value, chatMessage.value, chatHistory.value, chatModel.value);

      chatQuestions.value = Array.isArray(response.qa_data) ? response.qa_data.map(item => item.question) : [];
      chatAnswers.value = Array.isArray(response.qa_data) ? response.qa_data.map(item => item.answer) : [];
      chatHistory.value = Array.isArray(response.chat_history) ? response.chat_history : [];
      
      const lastAssistantMessage = response.chat_history?.slice().reverse().find(msg => msg.role === 'assistant');
      const responseMessage = lastAssistantMessage?.content || response.message || '';
      
      if (responseMessage) {
        chatResponse.value = responseMessage;
      }
      
      saveToCache();
      return responseMessage;
    } catch (error) {
      console.error('Error sending chat question:', error);
      return '';
    }
  }

  const initializeWebSocket = (listingId) => {
    if (!userInfo) {
      console.warn('Cannot initialize WebSocket: user not logged in');
      return null;
    }

    if (!socketInstance) {
      const handlers = {
        handleChatResponse,
      };

      socketInstance = new Socket(handlers);
    }

    chatSessionId.value = `${userInfo?.id}:${listingId}`;

    socketInstance.connectChat(userInfo, chatSessionId.value);
    
    return socketInstance;
  };

  const disconnectWebSocket = () => {
    if (socketInstance) {
      socketInstance.disconnectChat();
      socketInstance = null;
    }
  };

  const initializeChat = (listingId) => {
    if (!Array.isArray(chatHistory.value)) {
      chatHistory.value = [];
    }
    
    chatListingId.value = listingId;
    
    initializeWebSocket(listingId);
    
    loadFromCache();
    
    window.addEventListener('storage', handleStorageChange);
    
    const cleanup = () => {
      window.removeEventListener('storage', handleStorageChange);
      disconnectWebSocket();
      chatHistory.value = [];
      chatQuestions.value = [];
      chatAnswers.value = [];
      chatSessionId.value = null;
      chatListingId.value = null;
      chatMessage.value = null;
      chatModel.value = null;
    };
    
    return cleanup;
  };

  return {
    // readonly state
    chatHistory: readonly(chatHistory),
    chatQuestions: readonly(chatQuestions),
    chatAnswers: readonly(chatAnswers),
    chatSessionId: readonly(chatSessionId),
    chatListingId: readonly(chatListingId),
    chatMessage: readonly(chatMessage),
    chatResponse: readonly(chatResponse),
    chatModel: readonly(chatModel),
    isLoading: readonly(isLoading),
    
    // methods
    getChatQuestionsAndAnswers,
    initializeChat,
    sendChatQuestion,
    handleChatResponse,
    disconnectWebSocket,
  };
}